import React, { useMemo, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  closestCorners,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import Iconify from "../components/iconify/Iconify";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import {
  useGetTasksQuery,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
} from "../features/tasks/tasksApiSlice";
import { setDialog } from "../features/dialog/dialogSlice";
import useAuth from "../hooks/useAuth";
import TaskFormDialog from "../components/tasks/TaskFormDialog";
import TaskDetailDialog from "../components/tasks/TaskDetailDialog";

const COLUMNS = [
  {
    key: "NEU",
    label: "Neu",
    color: "#90caf9",
    icon: "mdi:plus-circle-outline",
  },
  {
    key: "IN_BEARBEITUNG",
    label: "In Bearbeitung",
    color: "#ffb74d",
    icon: "mdi:progress-clock",
  },
  {
    key: "IN_PRUEFUNG",
    label: "In Prüfung",
    color: "#ce93d8",
    icon: "mdi:eye-check-outline",
  },
  {
    key: "ERLEDIGT",
    label: "Erledigt",
    color: "#81c784",
    icon: "mdi:check-circle-outline",
  },
];

const PRIORITY_CONFIG = {
  dringend: { label: "Dringend", color: "error", icon: "mdi:alert-circle" },
  hoch: { label: "Hoch", color: "warning", icon: "mdi:arrow-up-bold" },
  normal: { label: "Normal", color: "info", icon: "mdi:minus" },
  niedrig: { label: "Niedrig", color: "success", icon: "mdi:arrow-down-bold" },
};

export default function TasksPage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { isMember } = useAuth();

  const { data: tasks = [], isLoading, isError, refetch } = useGetTasksQuery();
  const [updateTask] = useUpdateTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();

  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("ALL");
  const [mobileColumn, setMobileColumn] = useState("NEU");
  const [activeDragTask, setActiveDragTask] = useState(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 8 },
    }),
  );

  // Dialogs
  const [openForm, setOpenForm] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [detailTask, setDetailTask] = useState(null);

  const filtered = useMemo(() => {
    let arr = Array.isArray(tasks) ? tasks : [];
    if (priorityFilter !== "ALL") {
      arr = arr.filter((t) => t.priority === priorityFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(
        (t) =>
          (t.title || "").toLowerCase().includes(q) ||
          (t.description || "").toLowerCase().includes(q) ||
          (t.category || "").toLowerCase().includes(q),
      );
    }
    return arr;
  }, [tasks, search, priorityFilter]);

  const columnTasks = useMemo(() => {
    const map = {};
    COLUMNS.forEach((c) => (map[c.key] = []));
    filtered.forEach((t) => {
      if (map[t.status]) map[t.status].push(t);
    });
    // Sort each column: dringend first, then by dueDate
    const priorityOrder = { dringend: 0, hoch: 1, normal: 2, niedrig: 3 };
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => {
        const pa = priorityOrder[a.priority] ?? 2;
        const pb = priorityOrder[b.priority] ?? 2;
        if (pa !== pb) return pa - pb;
        if (a.dueDate && b.dueDate)
          return new Date(a.dueDate) - new Date(b.dueDate);
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return 0;
      }),
    );
    return map;
  }, [filtered]);

  const taskById = useMemo(() => {
    const map = new Map();
    (Array.isArray(tasks) ? tasks : []).forEach((task) => {
      map.set(String(task._id || task.id), task);
    });
    return map;
  }, [tasks]);

  const handleCreate = () => {
    setEditTask(null);
    setOpenForm(true);
  };

  const handleEdit = (task) => {
    setEditTask(task);
    setOpenForm(true);
  };

  const handleOpenDetail = (task) => {
    setDetailTask(task);
    setOpenDetail(true);
  };

  const handleStatusChange = useCallback(
    async (task, newStatus) => {
      try {
        await updateTask({ id: task._id || task.id, status: newStatus }).unwrap();
      } catch (e) {
        dispatch(
          setDialog({
            type: "ERROR",
            title: "Fehler",
            data: {
              errorText:
                e?.data?.message || "Status konnte nicht geändert werden.",
            },
          }),
        );
      }
    },
    [updateTask, dispatch],
  );

  const handleDelete = async (task) => {
    const ok = window.confirm(`Aufgabe "${task.title}" wirklich löschen?`);
    if (!ok) return;
    try {
      await deleteTask(task._id).unwrap();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Aufgabe gelöscht",
          data: { successText: `"${task.title}" wurde gelöscht.` },
        }),
      );
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler beim Löschen",
          data: { errorText: e?.data?.message || "Löschen fehlgeschlagen." },
        }),
      );
    }
  };

  const handleDragStart = useCallback(
    ({ active }) => {
      const activeTask =
        active?.data?.current?.task || taskById.get(String(active?.id));
      setActiveDragTask(activeTask || null);
    },
    [taskById],
  );

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (!over) return;

      const activeTask =
        active?.data?.current?.task || taskById.get(String(active?.id));
      const currentStatus = active?.data?.current?.status || activeTask?.status;
      const newStatus =
        over?.data?.current?.columnKey || over?.data?.current?.status;

      if (!activeTask || !newStatus || currentStatus === newStatus) {
        setActiveDragTask(null);
        return;
      }
      handleStatusChange(activeTask, newStatus);
      setActiveDragTask(null);
    },
    [handleStatusChange, taskById],
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragTask(null);
  }, []);

  return (
    <>
      <Helmet>
        <title>Dashboard: Aufgaben</title>
      </Helmet>

      <Container sx={{ pb: 4 }} maxWidth="xl">
        {/* Intro Card */}
        <Card
          variant="outlined"
          sx={{
            mb: 3,
            p: { xs: 2, md: 2.5 },
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.light, 0.08),
            borderColor: alpha(theme.palette.primary.main, 0.2),
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ mb: 0.5 }}>
                Aufgaben
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isMember
                  ? "Hier sehen Sie nur die Aufgaben, die Ihnen zugewiesen sind."
                  : "Organisieren Sie Aufgaben im Team, verfolgen Sie den Status und behalten Sie Deadlines im Blick."}
              </Typography>
            </Box>
            {!isMember && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCreate}
                startIcon={<Iconify icon="mdi:plus" width={20} />}
              >
                Neue Aufgabe
              </Button>
            )}
          </Stack>
        </Card>

        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            {filtered.length} Aufgaben gesamt
          </Typography>
        </Stack>

        {/* Filters */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Suche (Titel, Beschreibung, Kategorie)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <Iconify
                  icon="mdi:magnify"
                  width={20}
                  sx={{ mr: 1, opacity: 0.5 }}
                />
              ),
            }}
          />
          <TextField
            size="small"
            select
            label="Priorität"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="ALL">Alle</MenuItem>
            <MenuItem value="dringend">Dringend</MenuItem>
            <MenuItem value="hoch">Hoch</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="niedrig">Niedrig</MenuItem>
          </TextField>
        </Stack>

        {/* Status Summary */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2, flexWrap: "wrap" }}
          useFlexGap
        >
          {COLUMNS.map((col) => (
            <Chip
              key={col.key}
              label={`${col.label}: ${columnTasks[col.key]?.length || 0}`}
              icon={<Iconify icon={col.icon} width={16} />}
              sx={{
                borderColor: alpha(col.color, 0.5),
                bgcolor: alpha(col.color, 0.08),
              }}
              variant="outlined"
            />
          ))}
        </Stack>

        <Card
          variant="outlined"
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.light, 0.08),
            borderColor: alpha(theme.palette.info.main, 0.25),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Iconify icon="mdi:gesture-tap-hold" width={18} />
            <Typography variant="body2" color="text.secondary">
              Aufgaben per Drag-and-Drop zwischen Spalten verschieben (mobil:
              kurz halten und ziehen). Die Pfeile bewegen Aufgaben per Klick
              schrittweise nach links oder rechts.
            </Typography>
          </Stack>
        </Card>

        {/* Loading / Error */}
        {isLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Laden…</Typography>
          </Box>
        )}
        {isError && (
          <Typography color="error" sx={{ mb: 2 }}>
            Fehler beim Laden.{" "}
            <Button size="small" onClick={refetch}>
              Erneut laden
            </Button>
          </Typography>
        )}

        {/* Mobile column picker */}
        {isMobile && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 2, overflowX: "auto", pb: 1 }}
          >
            {COLUMNS.map((col) => (
              <Chip
                key={col.key}
                label={`${col.label} (${columnTasks[col.key]?.length || 0})`}
                onClick={() => setMobileColumn(col.key)}
                color={mobileColumn === col.key ? "primary" : "default"}
                variant={mobileColumn === col.key ? "filled" : "outlined"}
                sx={{
                  borderColor: alpha(col.color, 0.5),
                  bgcolor:
                    mobileColumn === col.key
                      ? alpha(col.color, 0.2)
                      : "transparent",
                }}
              />
            ))}
          </Stack>
        )}

        {/* Kanban Board */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              overflowX: isMobile ? "hidden" : "auto",
              pb: 2,
              minHeight: 400,
            }}
          >
            {COLUMNS.filter((col) => !isMobile || col.key === mobileColumn).map(
              (col) => (
                <KanbanColumn
                  key={col.key}
                  column={col}
                  tasks={columnTasks[col.key] || []}
                  isMobile={isMobile}
                  isMember={isMember}
                  onEdit={handleEdit}
                  onDetail={handleOpenDetail}
                  onDelete={handleDelete}
                  onStatusChange={handleStatusChange}
                />
              ),
            )}
          </Box>
          <DragOverlay
            dropAnimation={{
              duration: 180,
              easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
            }}
            zIndex={1800}
          >
            {activeDragTask ? <TaskDragPreview task={activeDragTask} /> : null}
          </DragOverlay>
        </DndContext>

        {/* Empty state */}
        {!isLoading && tasks.length === 0 && (
          <Card variant="outlined" sx={{ p: 3, textAlign: "center", mt: 2 }}>
            <Iconify
              icon="mdi:clipboard-text-outline"
              width={48}
              sx={{ opacity: 0.4 }}
            />
            <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
              Noch keine Aufgaben vorhanden
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              {isMember
                ? "Ihnen wurden noch keine Aufgaben zugewiesen."
                : "Erstellen Sie eine neue Aufgabe, um loszulegen."}
            </Typography>
            {!isMember && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCreate}
                startIcon={<Iconify icon="mdi:plus" width={20} />}
              >
                Neue Aufgabe
              </Button>
            )}
          </Card>
        )}
      </Container>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <TaskFormDialog
          onClose={() => setOpenForm(false)}
          onSaved={() => {
            setOpenForm(false);
            setEditTask(null);
          }}
          initialTask={editTask}
        />
      </Dialog>

      {/* Detail Dialog */}
      {!!detailTask && (
        <TaskDetailDialog
          open={openDetail}
          onClose={() => {
            setOpenDetail(false);
            setDetailTask(null);
          }}
          taskId={detailTask._id || detailTask.id}
        />
      )}
    </>
  );
}

// ---- Kanban Column ----
function KanbanColumn({
  column,
  tasks,
  isMobile,
  isMember,
  onEdit,
  onDetail,
  onDelete,
  onStatusChange,
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: `column-${column.key}`,
    data: { columnKey: column.key },
  });

  return (
    <Paper
      ref={setNodeRef}
      elevation={0}
      sx={{
        minWidth: isMobile ? "100%" : 280,
        maxWidth: isMobile ? "100%" : 320,
        flex: isMobile ? "1 1 100%" : "1 1 280px",
        bgcolor: "action.hover",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 300px)",
        border: "2px dashed transparent",
        borderColor: isOver ? alpha(column.color, 0.65) : "transparent",
        boxShadow: isOver ? `0 0 0 3px ${alpha(column.color, 0.18)}` : "none",
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}
    >
      {/* Column Header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `3px solid ${column.color}`,
        }}
      >
        <Iconify icon={column.icon} width={20} sx={{ color: column.color }} />
        <Typography variant="subtitle1" fontWeight={700}>
          {column.label}
        </Typography>
        <Chip size="small" label={tasks.length} sx={{ ml: "auto" }} />
      </Stack>

      {/* Task Cards */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {tasks.map((task) => (
          <TaskCard
            key={task._id || task.id}
            task={task}
            isMember={isMember}
            onEdit={onEdit}
            onDetail={onDetail}
            onDelete={onDelete}
            onStatusChange={onStatusChange}
          />
        ))}
        {tasks.length === 0 && (
          <Typography
            variant="body2"
            sx={{ textAlign: "center", color: "text.disabled", py: 4 }}
          >
            Keine Aufgaben
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ---- Task Card ----
function TaskCard({
  task,
  isMember,
  onEdit,
  onDetail,
  onDelete,
  onStatusChange,
}) {
  const prioConf = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normal;
  const isOverdue =
    task.dueDate &&
    !task.completedAt &&
    dayjs(task.dueDate).isBefore(dayjs(), "day");
  const assignees = Array.isArray(task.assignedTo) ? task.assignedTo : [];

  const currentIdx = COLUMNS.findIndex((c) => c.key === task.status);
  const canMoveLeft = currentIdx > 0;
  const canMoveRight = currentIdx < COLUMNS.length - 1;
  const taskId = String(task._id || task.id);
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: taskId,
      data: { task, status: task.status },
    });

  const transformStyle = transform ? CSS.Translate.toString(transform) : null;

  return (
    <Card
      ref={setNodeRef}
      variant="outlined"
      sx={{
        borderLeft: (theme) =>
          `4px solid ${theme.palette[prioConf.color]?.main || "#2196f3"}`,
        "&:hover": { boxShadow: 4, transform: "translateY(-2px)" },
        transition: "box-shadow 0.2s, transform 0.2s",
        opacity: isDragging ? 0.2 : 1,
        transform: transformStyle,
        zIndex: isDragging ? 2 : 1,
      }}
    >
      <CardActionArea
        onClick={() => {
          if (!isDragging) onDetail(task);
        }}
        {...listeners}
        {...attributes}
        sx={{ cursor: "grab", touchAction: "manipulation" }}
      >
        <CardContent sx={{ pb: "8px !important", pt: 1.5, px: 2 }}>
          {/* Priority + Category */}
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 0.5 }}
          >
            <Chip
              size="small"
              icon={<Iconify icon={prioConf.icon} width={14} />}
              label={prioConf.label}
              color={prioConf.color}
              variant="outlined"
              sx={{
                height: 22,
                "& .MuiChip-label": { px: 0.5, fontSize: "0.7rem" },
              }}
            />
            {task.category && (
              <Chip
                size="small"
                label={task.category}
                variant="outlined"
                sx={{
                  height: 22,
                  "& .MuiChip-label": { px: 0.5, fontSize: "0.7rem" },
                }}
              />
            )}
          </Stack>

          {/* Title */}
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, lineHeight: 1.3, mb: 0.5 }}
          >
            {task.title}
          </Typography>

          {/* Description preview */}
          {task.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.4,
              }}
            >
              {task.description}
            </Typography>
          )}

          {/* Bottom row: due date, assignees, comments count */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {task.dueDate && (
                <Chip
                  size="small"
                  icon={<Iconify icon="mdi:calendar" width={14} />}
                  label={dayjs(task.dueDate).format("DD.MM.YY")}
                  color={isOverdue ? "error" : "default"}
                  variant={isOverdue ? "filled" : "outlined"}
                  sx={{
                    height: 22,
                    "& .MuiChip-label": { px: 0.5, fontSize: "0.7rem" },
                  }}
                />
              )}
              {task.comments?.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.3}>
                  <Iconify
                    icon="mdi:comment-outline"
                    width={14}
                    sx={{ opacity: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {task.comments.length}
                  </Typography>
                </Stack>
              )}
            </Stack>
            {assignees.length > 0 && (
              <AvatarGroup
                max={3}
                sx={{
                  "& .MuiAvatar-root": {
                    width: 24,
                    height: 24,
                    fontSize: "0.7rem",
                  },
                }}
              >
                {assignees.map((u) => (
                  <Tooltip
                    key={u._id}
                    title={`${u.firstName || ""} ${u.lastName || ""}`}
                  >
                    <Avatar sx={{ width: 24, height: 24, fontSize: "0.7rem" }}>
                      {(u.firstName?.[0] || "").toUpperCase()}
                      {(u.lastName?.[0] || "").toUpperCase()}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>

      {/* Quick actions bar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 0.5, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Stack direction="row" spacing={0}>
          {canMoveLeft && (
            <Tooltip
              title={`Nach links verschieben (Klick): ${
                COLUMNS[currentIdx - 1].label
              }`}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(task, COLUMNS[currentIdx - 1].key);
                }}
              >
                <Iconify icon="mdi:chevron-left" width={18} />
              </IconButton>
            </Tooltip>
          )}
          {canMoveRight && (
            <Tooltip
              title={`Nach rechts verschieben (Klick): ${
                COLUMNS[currentIdx + 1].label
              }`}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(task, COLUMNS[currentIdx + 1].key);
                }}
              >
                <Iconify icon="mdi:chevron-right" width={18} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {!isMember && (
          <Stack direction="row" spacing={0}>
            <Tooltip title="Bearbeiten">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(task);
                }}
              >
                <Iconify icon="mdi:pencil-outline" width={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Löschen">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(task);
                }}
              >
                <Iconify icon="mdi:trash-can-outline" width={16} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function TaskDragPreview({ task }) {
  const prioConf = PRIORITY_CONFIG[task?.priority] || PRIORITY_CONFIG.normal;

  return (
    <Card
      variant="outlined"
      sx={{
        width: 280,
        borderLeft: (theme) =>
          `4px solid ${theme.palette[prioConf.color]?.main || "#2196f3"}`,
        boxShadow: 8,
        pointerEvents: "none",
      }}
    >
      <CardContent sx={{ pb: "10px !important", pt: 1.5, px: 2 }}>
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.75 }}>
          <Chip
            size="small"
            icon={<Iconify icon={prioConf.icon} width={14} />}
            label={prioConf.label}
            color={prioConf.color}
            variant="outlined"
            sx={{ height: 22, "& .MuiChip-label": { px: 0.5, fontSize: "0.7rem" } }}
          />
          {task?.category && (
            <Chip
              size="small"
              label={task.category}
              variant="outlined"
              sx={{ height: 22, "& .MuiChip-label": { px: 0.5, fontSize: "0.7rem" } }}
            />
          )}
        </Stack>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {task?.title}
        </Typography>
      </CardContent>
    </Card>
  );
}
